import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import classNames from 'classnames';
import { useCallback } from 'react';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { usePageContext } from 'components/page/PageContext';
import {
  SortableContainer,
  SortableInfo,
  SortableItem,
  SortableItemType,
  SortableList,
  SortableListInfo,
  SortableListType,
} from 'components/sortables/Sortable';
import { ISection } from 'features/page/sectionAPI';
import {
  patchProjectAsync,
  postProjectAsync,
} from 'features/project/projectSlice';
import {
  getFareyOrderByIndex,
  getFareyOrderUsingId,
  getFareyOrderUsingIndex,
} from 'helpers/farey-sorting';
import { AnyOrderObject, sameId } from 'helpers/objects';
import { sortByOrder } from 'helpers/sorting';
import { useSite } from 'selectors/siteSelectors';
import { useUser } from 'selectors/userSelectors';
import Project from './Project';

import { IProject } from 'features/project/projectAPI';
import { FormattedMessage } from 'react-intl';
import '../static/StaticPage.scss';
import './../page/Pages.scss';

type ProjectListProps = {
  className?: string;
  asPage?: boolean;
  pageRef?: React.RefObject<HTMLDivElement>;
};

export default function ProjectList(props: ProjectListProps) {
  const { className, asPage = false, pageRef } = props;

  const { locale, user, userIsManager } = useUser();
  const dispatch = useAppDispatch();
  const { projects } = useSite();
  const pageContext = usePageContext();

  const reorderProjects = useCallback(
    async (
      sortableInfo: SortableInfo,
      sourceItem: AnyOrderObject,
      sourceList: SortableListInfo,
      destinationItem?: AnyOrderObject,
      destinationList?: SortableListInfo,
      copy?: boolean,
    ) => {
      console.log('reordering projects');
      const data: ISection = {
        ...sourceItem,
      };
      // set the new order attribute
      const before = destinationItem
        ? ['top', 'left'].includes(
            extractClosestEdge(
              sortableInfo.location.current.dropTargets[0].data,
            ) ?? '',
          )
        : false;
      data.farey_order = getFareyOrderUsingId(
        sortByOrder(
          destinationList?.items?.filter(
            (item) => !item.pinned,
          ) as AnyOrderObject[],
        ),
        sameId(sourceList, destinationList) ? sourceItem.id : -1, // -1 if moving between lists
        destinationItem ? destinationItem.id : -1,
        before,
      );
      dispatch(patchProjectAsync({ data: data })).then(
        (response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              // console.log('Blocks updated');
              break;
            case 500:
            default:
              // TODO: need a better action here, possibly force reload.
              console.log(
                'Page not updated due to internal error. Please reload the page.',
              );
              break;
          }
        },
      );
    },
    [dispatch],
  );

  // const updateTitle = useCallback(
  //   (e: React.FocusEvent<HTMLHeadingElement>) => {
  //     console.log('page:', page);
  //     const target = e.currentTarget;
  //     dispatch(
  //       patchPageAsync({
  //         data: {
  //           ...page,
  //           title: target.textContent ?? '',
  //         } as IPage,
  //       }),
  //     );
  //   },
  //   [dispatch, page],
  // );

  function handleAddProject(): void {
    const sortedProjects = sortByOrder((projects ?? []) as AnyOrderObject[]);
    dispatch(
      postProjectAsync({
        data: {
          author: user,
          title_en: '',
          title_nl: '',
          text_en: '',
          text_nl: '',
          farey_order: getFareyOrderUsingIndex(sortedProjects, -1, 0, true),
          startDate: new Date(),
          endDate: null,
          duration: '',
          status: 'active',
        },
      }),
    );
  }

  const handleSortByDate = useCallback(() => {
    const statusOrder = { concept: 0, active: 1, finished: 2, archived: 3 };
    const sortedProjects = sortByOrder((projects ?? []) as AnyOrderObject[]);
    const sortedByDate = [...sortedProjects].sort((a, b) => {
      // First, compare by status
      const statusA =
        statusOrder[(a as IProject).status as keyof typeof statusOrder] ?? 999;
      const statusB =
        statusOrder[(b as IProject).status as keyof typeof statusOrder] ?? 999;
      if (statusA !== statusB) {
        return statusA - statusB;
      }
      if (
        statusA === statusOrder['finished'] ||
        statusA === statusOrder['archived']
      ) {
        // Then, compare by endDate (most recent first)
        const dateA = new Date((a as IProject).endDate ?? new Date()).getTime();
        const dateB = new Date((b as IProject).endDate ?? new Date()).getTime();
        return dateB - dateA;
      }
      // Else, compare by startDate (most recent first)
      const dateA = new Date((a as IProject).startDate ?? new Date()).getTime();
      const dateB = new Date((b as IProject).startDate ?? new Date()).getTime();
      return dateB - dateA;
    });
    sortedByDate.forEach((project, index) => {
      const data: IProject = {
        ...project,
        farey_order: getFareyOrderByIndex(index),
      } as IProject;
      dispatch(patchProjectAsync({ data: data }));
    });
  }, [dispatch, projects]);

  const listInfo: SortableListInfo = {
    id: 1,
    listType: SortableListType.Projects,
    items: sortByOrder((projects ?? []) as AnyOrderObject[]),
  };
  const sorting = pageContext?.sorting ?? false;
  const sortableDisabled = !userIsManager || !sorting;
  const content = projects ? (
    <SortableContainer reorder={reorderProjects}>
      <SortableList
        className="page_sections projects"
        listInfo={listInfo}
        accepting={[SortableItemType.Project]}
        disabled={sortableDisabled}
        containerRef={pageRef}
      >
        {asPage ? (
          <section>
            <h1 className="page_header">
              <FormattedMessage id={'PROJECTS.PROJECTS'} />
            </h1>
          </section>
        ) : null}
        <div className="list_menu">
          {user ? (
            <Icon
              symbol={IconSymbol.plus}
              onClick={handleAddProject}
              size={20}
              hintProps={{ hint: { id: 'PROJECTS.ADD_PROJECT' } }}
            />
          ) : null}
          {userIsManager ? (
            <Icon
              symbol={IconSymbol.sortByTime}
              size={20}
              hintProps={{ hint: { id: 'GENERAL.SORTING_HINT' } }}
              onClick={handleSortByDate}
            />
          ) : null}
        </div>
        {listInfo.items?.map((project) => (
          <SortableItem
            key={project.id}
            listInfo={listInfo}
            item={project as AnyOrderObject}
            itemType={SortableItemType.Project}
            addHandle={false}
            disabled={sortableDisabled}
          >
            <Project project={project} />
          </SortableItem>
        ))}
      </SortableList>
    </SortableContainer>
  ) : null;

  if (!asPage) {
    return content;
  }

  return (
    <div className={classNames('static_page', className)}>
      <div className="page_container">
        <div className="page_content" ref={pageRef}>
          {content}
        </div>
      </div>
    </div>
  );
}
